<template>
<div class="animated fadeIn device-detail">
  <div class="row page-header">
    <div class="col-xl-8 col-12">
      <div class="row">
        <div class="col-sm-7 col-12">
          <h2 class="page-title float-left">{{ $options.name }} <small>Sensor Data</small></h2>
        </div>

        <div class="col-sm-5 col-12">
          <json-excel
            type="csv"
            :data="exportData()"
            :escapeCsv="false"
            class="float-right d-none d-md-block"
          ><b-button size="md" variant="success">Export</b-button>
          </json-excel>
          <b-button
            v-if="manualReadingsEnabled"
            size="md"
            variant="outline-primary"
            class="float-right mr-2 d-none d-md-inline-block"
            :disabled="localConfig.selectedDevices.length !== 1"
            :title="localConfig.selectedDevices.length === 1 ? 'Add a manual reading for the selected device' : 'Select exactly one device'"
            @click="openManualReadingModal"
          >
            <i class="fa fa-pencil-alt"></i> Add Reading
          </b-button>
          <div class="refresh-select float-md-right float-xs-left mr-2">
            <span class="switch-name">Auto Refresh?</span>
            <label class="switch switch-sm switch-text switch-info float-right mb-0 mr-1">
              <input class="switch-input" type="checkbox" v-model="localConfig.refreshEnabled">
              <span data-on="On" data-off="Off" class="switch-label"></span>
              <span class="switch-handle"></span>
            </label>
          </div>
        </div>
      </div><!-- /row -->

      <div class="row">
        <div class="col-lg-12">
          <multiselect
            id="device-select"
            v-model="localConfig.selectedDevices"
            :options="formattedDevices"
            :multiple="true"
            track-by="serial"
            label="name"
            placeholder="Select Devices"
          ></multiselect>
          <a v-if="localConfig.selectedDevices.length" href="#" class="small text-muted" @click.prevent="localConfig.selectedDevices = []">Deselect all</a>
        </div>
      </div>

      <div class="row">

        <div class="col-md-6 col-12">
          <div class="date-select mt-3">
            <date-picker
              v-model="dateRange"
              range
              confirm
              type="datetime"
              format="YYYY-MM-DD hh:mm"
              lang="en"
              width="320"
              input-class="form-control"
              range-separator="-"
              :not-after="maxDate"
              :shortcuts="dateShortcuts"
              :time-picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"
            ></date-picker>
          </div>
        </div>
        <div class="col-md-5 col-10">
          <div class="agg-select">
            <b-form-select v-model="localConfig.aggMethod" class="mb-3 mt-3 float-xs-left float-md-right" size="md" style="max-width: 20rem;">
              <option :value="null">Select aggregation options</option>
              <option :value="{period: null, fn: null}">Raw Data (no aggregation)</option>
              <optgroup label="Average">
                <option :value="{period: '15m', fn: 'mean'}">15 Minute Avg</option>
                <option :value="{period: '30m', fn: 'mean'}">30 Minute Avg</option>
                <option :value="{period: '1h', fn: 'mean'}">Hourly Avg</option>
                <option :value="{period: '1d', fn: 'mean'}">Daily Avg</option>
              </optgroup>
              <optgroup label="Max">
                <option :value="{period: '15m', fn: 'max'}">15 Minute Max</option>
                <option :value="{period: '30m', fn: 'max'}">30 Minute Max</option>
                <option :value="{period: '1h', fn: 'max'}">Hourly Max</option>
                <option :value="{period: '1d', fn: 'max'}">Daily Max</option>
              </optgroup>
              <optgroup label="Min">
                <option :value="{period: '15m', fn: 'min'}">15 Minute Min</option>
                <option :value="{period: '30m', fn: 'min'}">30 Minute Min</option>
                <option :value="{period: '1h', fn: 'min'}">Hourly Min</option>
                <option :value="{period: '1d', fn: 'min'}">Daily Min</option>
              </optgroup>
            </b-form-select>
          </div>
        </div>
        <div class="col-md-1 col-2">
          <b-button class="option-submit mt-3 float-right" variant="success" @click="refetchAll">Go</b-button>
        </div>

      </div><!-- /row -->

      <!-- Which well(s) the potentiometric head is being calculated against -->
      <div v-if="localConfig.selectedDevices.length" class="row">
        <div class="col-12">
          <p v-if="primaryWell" class="small text-muted mb-1">
            <i class="fa fa-link mr-1"></i>
            Potentiometric head correlated against <strong>{{ primaryWell.name }}</strong>
            <span v-if="relatedWells.length > 1" class="text-warning">
              — {{ relatedWells.length }} wells are linked; only this one is used.
            </span>
          </p>
          <div v-if="primaryWell" class="small mb-0">
            <b-form-checkbox v-model="baroCorrection" switch size="sm" class="d-inline-block">
              Correct river depth using <strong>{{ primaryWell.name }}</strong> barometric pressure
            </b-form-checkbox>
            <span v-if="baroCorrection && latestCorrectionFt !== null" class="text-muted ml-2">
              (shifting elevation by {{ latestCorrectionFt >= 0 ? '+' : '' }}{{ latestCorrectionFt.toFixed(2) }} ft right now)
            </span>
            <span v-else-if="baroCorrection" class="text-warning ml-2">
              (no well pressure available for this range — showing uncorrected)
            </span>
            <i id="baro-info" class="fa fa-info-circle text-muted ml-1" style="cursor:pointer;"></i>
            <b-tooltip target="baro-info" placement="right" triggers="hover">
              For a vented transducer whose vent tube ends inside a sealed enclosure: the device's own
              pressure reading is the reference the diaphragm is seeing, and the linked well's is true
              atmosphere. Their difference × 0.0335 ft/hPa is the error. Harmless once the enclosure
              is vented — the two pressures then agree and the correction goes to zero.
            </b-tooltip>
          </div>
          <p v-else class="small text-warning mb-0">
            <i class="fa fa-unlink mr-1"></i>
            No related well configured for the selected device{{ localConfig.selectedDevices.length > 1 ? 's' : '' }} —
            potentiometric head is unavailable. Link a well under Device Configuration.
          </p>
        </div>
      </div>

    </div><!-- /col-xl-8 -->

    <div class="col-xl-4 mb-2">
      <GoogleMap
        mapStyle="width:100%;height:140px;"
        :center="primaryDeviceLocation"
        :markers="deviceLocations"
      />
    </div>

  </div>

  <b-alert v-if="dataDiagnostic" show variant="warning" class="mb-3">
    <i class="fa fa-exclamation-triangle mr-1"></i>
    <span v-html="dataDiagnostic"></span>
  </b-alert>

  <div class="row">
    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="metricChart"
            v-if="!loading"
            :title="correctionActive ? 'Water Surface Elevation (baro-corrected)' : 'Water Surface Elevation'"
            :scale="'f'"
            :series="getWaterElevationSeries()"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Elevation</div>
              <strong>{{ getAverage(getWaterElevationSeries(), 2) }} ft</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="2" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta(getWaterElevationSeries()) }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="potentiometricChart"
            v-if="!loading"
            :title="correctionActive ? 'Potentiometric Head (baro-corrected)' : 'Potentiometric Head'"
            :scale="'f'"
            :series="getPotentiometricSeries()"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Potentiometric Head</div>
              <strong>{{ getAverage(getPotentiometricSeries(), 5) }} ft</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="2" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta(getPotentiometricSeries()) }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

  </div>

  <div class="row">
    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="batteryChart"
            v-if="!loading"
            :title="'Battery %'"
            :scale="'pct'"
            :series="getSeries('bat_chg')"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Charge</div>
              <strong>{{ getAverage(getSeries('bat_chg')) }} %</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta(getSeries('bat_chg')) }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="tempChart"
            v-if="!loading"
            :title="'Case Temperature'"
            :scale="'Fahrenheit'"
            :series="getSeries('tmp_f')"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Temperature</div>
              <strong>{{ getAverage(getSeries('tmp_f')) }} F</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta(getSeries('tmp_f')) }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

  </div>

  <div v-if="localConfig.selectedDevices.length === 1" class="row mt-3">
    <div class="col-12">
      <WeatherWidget :lat="primaryDeviceLocation.lat" :lng="primaryDeviceLocation.lng" :date-range="dateRange" />
    </div>
  </div>

  <ManualReadingModal
    :device="manualReadingDevice"
    :visible="showManualReadingModal"
    @close="showManualReadingModal = false"
    @created="onManualReadingChanged"
  />

</div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import LineChart from '@/components/charts/LineChart.vue'
import GoogleMap from '@/components/GoogleMap.vue'
import WeatherWidget from '@/components/WeatherWidget.vue'
import ManualReadingModal from '@/components/ManualReadingModal.vue'
import DatePicker from 'vue2-datepicker'
import moment from 'moment-timezone'
import JsonExcel from 'vue-json-excel'
import gql from 'graphql-tag'

import { METRICS_QUERY, RELATED_METRICS_QUERY, MANUAL_READINGS_QUERY } from '@/constants/graphql'
import { DATE_SHORTCUTS } from '@/constants/data'
import { MANUAL_READINGS_ENABLED } from '@/constants/features'

// Readings from the river device and the well are logged on independent schedules, so
// they rarely share a timestamp. Pair a river point with the nearest well point inside
// this window (seconds) when computing head.
const CORRELATION_WINDOW_S = 900

// Columns prepended to each export row ahead of the raw metric columns.
const EXPORT_HEADER = ['device_name', 'location', 'type', 'local_timestamp', 'river_elevation', 'well_elevation', 'potentiometric_head', 'baro_correction_ft']
const POTENTIOMETRIC_COL = 6

// Barometric correction for a vented transducer whose vent tube terminates inside a
// sealed enclosure. The diaphragm then references enclosure pressure instead of the
// atmosphere, and the device's own pres_hpa (BME in that same enclosure) reports exactly
// the wrong reference it is seeing. The linked well's pres_hpa is true atmosphere at the
// same site. The error in feet of water is their difference times this constant:
//   depth_true = depth_f - FT_PER_HPA * (river_pres_hpa - well_pres_hpa)
// Once the enclosure is vented the two pressures agree and the term collapses to ~0, so
// leaving this on is harmless after the fix. Intended as a stopgap, hence not persisted.
const FT_PER_HPA = 0.03346

// River devices reuse the well sensor type AND the well calibration convention:
// cal = -(D), where D = d + P is the sensor's depth below a surveyed benchmark
// (d = taped benchmark-to-water, P = the sensor's own water column). Firmware then
// publishes depth_f = |raw + cal| = |P - D| = d — a depth DOWN from the benchmark to
// the water surface, exactly as on a well. So both sides subtract:
//   river surface elevation = Device.elevation - depth_f   (elevation = benchmark)
//   well water table        = Device.elevation - depth_f   (elevation = casing top)
const DEPTH_METRIC = 'depth_f'

const GET_STATE = gql`
  query {
    config @client {
      river {
        refreshEnabled
        aggMethod {
          period
          fn
        }
        selectedDevices {
          name
          deviceName
          serial
        }
      }
    }
  }
`

export default {
  name: 'River',
  props: ['devices', 'defaultDevices'],
  data () {
    return {
      loading: 0,
      error: null,
      maxDate: new Date(Date.now() + 60000 * 30), // show up to 30 minutes in the future
      localConfig: {
        refreshEnabled: false,
        aggMethod: {
          period: '1h',
          fn: 'mean'
        },
        selectedDevices: this.defaultDevices
      },
      refreshInterval: 60000,
      refreshTimer: null,
      dateRange: [new Date(Date.now() - 3600 * 1000 * 24 * 7), new Date()], // 1 week back
      dateShortcuts: DATE_SHORTCUTS,
      showManualReadingModal: false,
      manualReadingDevice: null,
      manualReadings: [],
      // Stopgap for a sealed river enclosure — see FT_PER_HPA. Off by default now that the
      // enclosure is vented; left available as a manual toggle. Deliberately not persisted.
      baroCorrection: false
    }
  },
  watch: {
    localConfig: {
      handler: function(data) {

        const config = {
          river: {
            ...data,
            __typename: 'River',
            aggMethod: {
              ...data.aggMethod,
              __typename: 'RiverAggregation'
            },
            selectedDevices: data.selectedDevices.map((d) => {
              return Object.assign({ __typename: 'Device' }, d)
            })
          }
        }

        this.$apollo
          .mutate({
            mutation: gql`
              mutation($config: Config) {
                updateConfig(config: $config) @client
              }
            `,
            variables: {
              config
            }
          })

        this.updateRefresh()
      },
      deep: true
    }
  },
  computed: {
    manualReadingsEnabled () { return MANUAL_READINGS_ENABLED },
    primaryDeviceLocation () {
      return this.deviceLocations[0].position
    },
    deviceLocations () {
      if (this.localConfig.selectedDevices.length) {
        const searchSerials = this.localConfig.selectedDevices.map(x => x.serial)
        const mapDevices = this.devices
          .filter(d => searchSerials.includes(d.serial))
          .map(d => ({ position: { lat: d.latitude, lng: d.longitude } }))

        if (mapDevices.length) {
          return mapDevices
        }
      }
      // [{position: primaryDeviceLocation}]
      return [{ position: { lat: 44.030829, lng: -70.3535825 } }] // bottling plant
    },
    formattedDevices () {
      let deviceList = []
      this.devices.forEach((device) => {
        if(device.type == 'river') {
          deviceList.push({
            name: device.site.name + ' (Device: ' + device.name + ')',
            deviceName: device.name,
            serial: device.serial
          })
        }
      })
      return deviceList
    },
    selectedSerials () {
      return this.localConfig.selectedDevices.map(x => x.serial)
    },
    // Wells linked to the selected river devices via Device.relatedTo / relatedBy.
    // The pairing is treated as directionless — configuring it from either side
    // correlates the same way — and deduped so two rivers sharing a well query it once.
    relatedWells () {
      var self = this
      var wells = {}
      this.selectedSerials.forEach(function (serial) {
        var device = self.getDeviceBySerial(serial)
        if (!device) return
        var linked = (device.relatedTo || []).concat(device.relatedBy || [])
        linked.forEach(function (r) {
          if (r && r.type === 'well' && r.serial) wells[r.serial] = r
        })
      })
      return Object.keys(wells).map(function (s) { return wells[s] })
    },
    // Head is computed against a single well so the export keeps one row per river
    // reading. When several wells are linked the first is used rather than blending
    // them, which would produce a head figure that belongs to no actual well pair.
    primaryWell () {
      return this.relatedWells.length ? this.relatedWells[0] : null
    },
    // The benchmark elevation depth_f is measured down from. Without it a reading
    // cannot be turned into an elevation, so head cannot be computed.
    riverElevationMissing () {
      var self = this
      return this.selectedSerials.some(function (serial) {
        var d = self.getDeviceBySerial(serial)
        return !d || d.elevation === null || d.elevation === undefined
      })
    },
    // True when the toggle is on and there is a well to take atmosphere from.
    correctionActive () {
      return !!(this.baroCorrection && this.primaryWell)
    },
    // Correction currently being applied to the most recent river reading, expressed as
    // the shift in water surface elevation, for the toggle's label.
    latestCorrectionFt () {
      if (!this.correctionActive) return null
      var data = (this.metrics && this.metrics.data && this.metrics.data.length) ? this.metrics.data[0] : null
      if (!data || !data.values || !data.values.length) return null
      var timeIndex = this.getMetricIndex(data, 'time')
      var presIndex = this.getMetricIndex(data, this.metricKey('pres_hpa'))
      if (timeIndex < 0 || presIndex < 0) return null
      var last = data.values[data.values.length - 1]
      var well = this.nearestWellPoint(this.wellElevationPoints(), last[timeIndex])
      return this.baroCorrectionFt(last[presIndex], well)
    },
    // Columns the API actually returned, used to explain an empty chart.
    availableColumns () {
      var data = (this.metrics && this.metrics.data && this.metrics.data.length) ? this.metrics.data : null
      return (data && data[0] && data[0].columns) ? data[0].columns : []
    },
    // A missing column and an empty result set both render as a blank chart, because
    // getMetricIndex returns -1 and the series maps to undefined values. Tell them apart.
    dataDiagnostic () {
      if (!this.selectedSerials.length || this.loading) return null
      if (!this.metrics || !this.metrics.data || !this.metrics.data.length) {
        return 'No <code>river</code> readings were returned for this date range. Readings are stored ' +
          'in a per-type table, so anything this device recorded under a previous device type stays ' +
          'in that table and will not appear here.'
      }
      var wanted = this.metricKey(DEPTH_METRIC)
      if (this.availableColumns.indexOf(wanted) === -1) {
        return 'Readings were returned, but none carry a <code>' + wanted + '</code> column, which is ' +
          'what this page charts. Columns present: <code>' + this.availableColumns.join('</code>, <code>') +
          '</code>.'
      }
      if (this.riverElevationMissing) {
        return 'Readings are arriving, but this device has no <b>elevation</b> set. <code>depth_f</code> is ' +
          'measured down from the surveyed benchmark, so without that benchmark elevation it cannot be ' +
          'converted to a water surface elevation and both charts stay empty. Set it under Device Configuration.'
      }
      return null
    },
    labels () {
      let data = (this.metrics && this.metrics.data) ? this.metrics.data : null
      if (data) {
        let device = Object.keys(data)[0]
        if (data && device) return data[device].values.map(x => x[0])
      }
      return []
    }
  },
  apollo: {
    config:  {
      query: GET_STATE
    },
    metrics: {
      query: METRICS_QUERY,
      variables () {
        return {
          where: {
            device: this.selectedSerials.join('|'),
            type: 'river',
            metric: 'depth_f|pres_hpa|tmp_f|bat_chg',
            start: this.dateRange[0],
            end: this.dateRange[1],
            agg: this.localConfig.aggMethod.fn,
            period: this.localConfig.aggMethod.period,
            sort: 'asc',
            limit: 15000
          }
        }
      },
      skip () {
        return (!this.selectedSerials.length)
      }
    },
    relatedMetrics: {
      query: RELATED_METRICS_QUERY,
      variables () {
        return {
          where: {
            device: this.primaryWell ? this.primaryWell.serial : '',
            type: 'well',
            // pres_hpa is the well's true barometric, used to correct the river's depth
            metric: 'depth_f|pres_hpa',
            start: this.dateRange[0],
            end: this.dateRange[1],
            agg: this.localConfig.aggMethod.fn,
            period: this.localConfig.aggMethod.period,
            sort: 'asc',
            limit: 15000
          }
        }
      },
      skip () {
        return (!this.primaryWell)
      }
    },
    manualReadings: {
      query: MANUAL_READINGS_QUERY,
      variables () {
        return {
          where: {
            deviceSerial_in: this.selectedSerials,
            timestamp_gte: this.dateRange[0],
            timestamp_lte: this.dateRange[1]
          },
          orderBy: { timestamp: 'asc' },
          limit: 5000
        }
      },
      skip () {
        return (!MANUAL_READINGS_ENABLED || !this.selectedSerials.length)
      },
      error () { /* silently ignore */ }
    }
  },
  methods: {
    refetchAll () {
      this.$apollo.queries.metrics.refetch()
      if (this.primaryWell) {
        this.$apollo.queries.relatedMetrics.refetch()
      }
    },
    openManualReadingModal () {
      if (this.localConfig.selectedDevices.length !== 1) return
      const selected = this.localConfig.selectedDevices[0]
      const fullDevice = this.devices.find(d => d.serial === selected.serial)
      this.manualReadingDevice = fullDevice || { serial: selected.serial, name: selected.deviceName, type: 'river' }
      this.showManualReadingModal = true
    },
    onManualReadingChanged () {
      this.showManualReadingModal = false
      if (this.$apollo.queries.manualReadings) {
        this.$apollo.queries.manualReadings.refetch()
      }
    },
    manualSeriesForMetric (metric) {
      var self = this
      if (!self.manualReadings || !self.manualReadings.length) return []
      var bySerial = {}
      self.manualReadings.forEach(function (r) {
        if (r.metricName !== metric) return
        var serial = (r.device && r.device.serial) || null
        if (!serial) return
        if (!bySerial[serial]) bySerial[serial] = { verified: [], unverified: [] }
        var bucket = r.isFieldVerified ? 'verified' : 'unverified'
        bySerial[serial][bucket].push([new Date(r.timestamp).getTime(), r.value])
      })
      var series = []
      Object.keys(bySerial).forEach(function (serial) {
        var deviceObj = self.getDeviceBySerial(serial)
        var label = (deviceObj && deviceObj.name) || serial
        if (bySerial[serial].verified.length) {
          series.push({
            name: label + ' (field verified)',
            type: 'scatter',
            marker: { symbol: 'diamond', radius: 6, fillColor: '#28a745', lineColor: '#1d6e34', lineWidth: 1 },
            lineWidth: 0,
            data: bySerial[serial].verified
          })
        }
        if (bySerial[serial].unverified.length) {
          series.push({
            name: label + ' (manual)',
            type: 'scatter',
            marker: { symbol: 'diamond', radius: 6, fillColor: '#ffc107', lineColor: '#a8800a', lineWidth: 1 },
            lineWidth: 0,
            data: bySerial[serial].unverified
          })
        }
      })
      return series
    },
    updateRefresh () {
      if (this.localConfig.refreshEnabled && !this.refreshTimer) {
        this.refreshTimer = setInterval(() => {
          // push out date for date picker
          this.maxDate = new Date(Date.now() + 60000 * 30)
          this.dateRange = [
            moment(this.dateRange[0]).add(this.refreshInterval, 'ms').toDate(),
            moment(this.dateRange[1]).add(this.refreshInterval, 'ms').toDate()
          ]
          this.$apollo.queries.metrics.refetch()
        }, this.refreshInterval)
      } else {
        clearInterval(this.refreshTimer)
      }
    },
    // Column name the API returns for a metric under the current aggregation
    // (e.g. `mean_depth_f` when averaging, plain `depth_f` for raw data).
    metricKey (metric) {
      return (this.localConfig.aggMethod && this.localConfig.aggMethod.fn)
        ? this.localConfig.aggMethod.fn + '_' + metric
        : metric
    },
    // Timestamps land on arbitrary seconds; round each up to the aggregation
    // boundary so exported rows line up with the chart buckets.
    formatBucketTime (epochSeconds) {
      var period = this.localConfig.aggMethod.period
      var time = moment(epochSeconds * 1000).second(0)
      var minutes = { '15m': 15, '30m': 30, '1h': 60, '1d': 1440 }[period]
      if (!minutes) return time.format('YYYY-MM-DD HH:mm:ss')
      return time.add(minutes - (time.minute() % minutes), 'minutes').format('YYYY-MM-DD HH:mm:ss')
    },
    // Water-table elevation at each related well: survey elevation minus depth-to-water,
    // plus the well's barometric pressure. Returned as [epochSeconds, elevation, pres_hpa]
    // sorted ascending so lookups can scan it.
    wellElevationPoints () {
      var self = this
      var raw = (this.relatedMetrics && this.relatedMetrics.data && this.relatedMetrics.data.length) ? this.relatedMetrics.data : []
      var points = []

      raw.forEach(function (d) {
        var deviceObj = self.getDeviceBySerial(d.tags.device_id) ||
          self.relatedWells.find(function (w) { return w.serial === d.tags.device_id })
        if (!deviceObj || deviceObj.elevation === null || deviceObj.elevation === undefined) return

        var timeIndex = self.getMetricIndex(d, 'time')
        var depthIndex = self.getMetricIndex(d, self.metricKey('depth_f'))
        var presIndex = self.getMetricIndex(d, self.metricKey('pres_hpa'))
        if (timeIndex < 0 || depthIndex < 0) return

        d.values.forEach(function (values) {
          var depth = values[depthIndex]
          if (depth === null || depth === undefined) return
          var pres = (presIndex >= 0) ? values[presIndex] : null
          points.push([values[timeIndex], deviceObj.elevation - depth, pres])
        })
      })

      return points.sort(function (a, b) { return a[0] - b[0] })
    },
    // Nearest well point ([time, elevation, pres_hpa]) to `epochSeconds`, or null when
    // nothing falls inside the correlation window.
    nearestWellPoint (points, epochSeconds) {
      var best = null
      var bestGap = CORRELATION_WINDOW_S
      for (var i = 0; i < points.length; i++) {
        var gap = Math.abs(points[i][0] - epochSeconds)
        if (gap <= bestGap) {
          bestGap = gap
          best = points[i]
        } else if (points[i][0] > epochSeconds) {
          break // sorted ascending — everything past here is further away
        }
      }
      return best
    },
    wellElevationAt (points, epochSeconds) {
      var p = this.nearestWellPoint(points, epochSeconds)
      return p ? p[1] : null
    },
    // Shift to apply for the enclosure-pressure error, in feet of water. Positive means
    // the river's reference pressure was above atmosphere. Null when the correction is
    // off or either pressure is unavailable — callers then fall back to the raw depth.
    baroCorrectionFt (riverPres, wellPoint) {
      if (!this.correctionActive) return null
      if (riverPres === null || riverPres === undefined) return null
      var wellPres = wellPoint ? wellPoint[2] : null
      if (wellPres === null || wellPres === undefined) return null
      return FT_PER_HPA * (riverPres - wellPres)
    },
    exportData () {
      var self = this
      var raw = (this.metrics && this.metrics.data && this.metrics.data.length) ? this.metrics.data : []
      var processed = []
      var wellPoints = this.wellElevationPoints()

      raw.forEach(function (d) {
        var deviceObj = self.getDeviceBySerial(d.tags.device_id)
        var timeIndex = self.getMetricIndex(d, 'time')
        var depthIndex = self.getMetricIndex(d, self.metricKey(DEPTH_METRIC))
        var presIndex = self.getMetricIndex(d, self.metricKey('pres_hpa'))
        if (timeIndex < 0) return

        if (!processed.length) {
          processed.push(EXPORT_HEADER.concat(d.columns))
        }

        var datumElevation = (deviceObj && deviceObj.elevation !== null && deviceObj.elevation !== undefined)
          ? deviceObj.elevation
          : null

        d.values.forEach(function (values) {
          var timeStr = self.formatBucketTime(values[timeIndex])
          var depth = (depthIndex >= 0) ? values[depthIndex] : null

          // The well point supplies both the water-table elevation for head and the true
          // barometric for the river's enclosure-pressure correction.
          var wellPoint = self.nearestWellPoint(wellPoints, values[timeIndex])
          var correction = self.baroCorrectionFt((presIndex >= 0) ? values[presIndex] : null, wellPoint)
          if (correction !== null && depth !== null && depth !== undefined) {
            depth = depth - correction
          }

          // depth_f is measured down from the benchmark, so the water surface elevation
          // is the benchmark elevation minus the reading. Without the benchmark
          // elevation on the device record, head is not computable.
          var riverElevation = (datumElevation !== null && depth !== null && depth !== undefined)
            ? datumElevation - depth
            : null

          var wellElevation = wellPoint ? wellPoint[1] : null
          var potentiometric = (wellElevation !== null && riverElevation !== null)
            ? wellElevation - riverElevation
            : null

          processed.push([
            deviceObj ? deviceObj.name : d.tags.device_id,
            (deviceObj && deviceObj.site) ? deviceObj.site.name : null,
            d.name,
            timeStr,
            riverElevation,
            wellElevation,
            potentiometric,
            correction
          ].concat(values))
        })
      })

      return processed
    },
    // Head difference between the aquifer (related well) and the river surface.
    // Positive means the water table sits above the river — the aquifer is discharging to it.
    getPotentiometricSeries () {
      var rows = this.exportData()
      if (rows.length < 2) return []

      var raw = this.metrics.data
      var timeIndex = this.getMetricIndex(raw[0], 'time')
      if (timeIndex < 0) return []
      var rowTimeIndex = EXPORT_HEADER.length + timeIndex

      var series = [{ data: [], name: rows[1][0] }]
      rows.slice(1).forEach(function (row) {
        var head = row[POTENTIOMETRIC_COL]
        if (head === null || head === undefined || isNaN(head)) return
        series[0].data.push([row[rowTimeIndex] * 1000, head])
      })

      return series[0].data.length ? series : []
    },
    // Water surface elevation = benchmark elevation - depth_f, the same derivation a
    // well uses for its water table, with the enclosure-pressure correction applied when
    // active. Built from the raw rows rather than getSeries() because the correction
    // needs the river's pres_hpa from the same row. Manual-reading overlays are dropped:
    // they are recorded as depths, so plotting them on an elevation axis would mislead.
    getWaterElevationSeries () {
      var self = this
      var raw = (this.metrics && this.metrics.data && this.metrics.data.length) ? this.metrics.data : []
      var wellPoints = this.correctionActive ? this.wellElevationPoints() : []
      var series = []

      raw.forEach(function (d) {
        var deviceObj = self.getDeviceBySerial(d.tags.device_id)
        if (!deviceObj || deviceObj.elevation === null || deviceObj.elevation === undefined) return
        var timeIndex = self.getMetricIndex(d, 'time')
        var depthIndex = self.getMetricIndex(d, self.metricKey(DEPTH_METRIC))
        var presIndex = self.getMetricIndex(d, self.metricKey('pres_hpa'))
        if (timeIndex < 0 || depthIndex < 0) return

        series.push({
          name: deviceObj.name,
          device: deviceObj,
          data: d.values.map(function (values) {
            var depth = values[depthIndex]
            if (depth === null || depth === undefined) return [values[timeIndex] * 1000, null]
            var wellPoint = self.nearestWellPoint(wellPoints, values[timeIndex])
            var correction = self.baroCorrectionFt((presIndex >= 0) ? values[presIndex] : null, wellPoint)
            if (correction !== null) depth = depth - correction
            return [values[timeIndex] * 1000, deviceObj.elevation - depth]
          })
        })
      })

      return series
    },
    getSeries (metric, series=this.metrics) {
      metric = metric || DEPTH_METRIC
      const timeIndex = 2

      let seriesData = []
      let metricName = this.metricKey(metric)

      if (!series || !series.data) {
        return seriesData
      }

      Object.keys(series.data).forEach((device) => {
        let deviceData = series.data[device]
        let deviceObj = this.getDeviceBySerial(deviceData.tags.device_id)
        let metricIndex = this.getMetricIndex(deviceData, metricName)

        seriesData.push({
          name: (deviceObj) ? deviceObj.name : deviceData.tags.device_id,
          device: (deviceObj) ? deviceObj : null,
          data: deviceData.values.map(x => [x[timeIndex] * 1000, x[metricIndex]])
        })
      })
      // Overlay manual readings as additional scatter series (skip when comparing against relatedMetrics)
      if (series === this.metrics) {
        var manualSeries = this.manualSeriesForMetric(metric)
        if (manualSeries.length) {
          manualSeries.forEach(function (s) { seriesData.push(s) })
        }
      }
      return seriesData
    },
    getValue (series, latest) {
      latest = (latest !== undefined) ? latest : true

      if (!series || series.length === 0) {
        return null
      }

      if (latest) {
        try {
          let point = series[0].data.slice().reverse().find(el => el[1] !== null)
          if (point && point.length) return point[1]
        } catch (e) {
          return 0
        }
      }

      let sliceIndex = (latest) ? -4 : 1

      let values = series.map((d) => {
        return d.data.slice(sliceIndex)[0][1]
      })

      if (values.length > 0) {
        let sum = values.reduce((a, b) => a + b, 0)
        return parseFloat((sum / values.length).toFixed(2))
      }

      return 0
    },
    getAverage (series, decimals) {
      decimals = (decimals !== undefined) ? decimals : 1

      if (!series || !series[0]) {
        return null
      }

      let avgSum = 0

      series.forEach(s => {
        let values = s.data.map(d => d[1])
        let sum = values.reduce((a, b) => a + b, 0)
        avgSum += parseFloat((sum / values.length).toFixed(decimals))
      })

      return parseFloat((avgSum / series.length).toFixed(decimals))
    },
    getDelta (series) {
      let start = this.getValue(series, false)
      let end = this.getValue(series, true)

      if (start === 0) {
        return '0%'
      }

      let delta = 100 * Math.abs( ( end - start ) / ( (end + start) / 2 ) )

      if (delta === 0) {
        return '0%'
      } else {
        return (delta > 0) ? `+${delta.toFixed(1)}%` : `${delta.toFixed(2)}%`
      }
    },
    getDeviceBySerial (serial) {
      return this.devices.filter((device) => {
        return device.serial === serial
      })[0]
    },
    // Numeric on purpose — `for...in` yields string keys, and this index is used in
    // arithmetic (EXPORT_HEADER.length + timeIndex) where a string would concatenate.
    getMetricIndex (deviceData, metricName) {
      for (let i = 0; i < deviceData.columns.length; i++) {
        if (deviceData.columns[i] === metricName) {
          return i
        }
      }
      return -1
    }
  },
  beforeMount () {
    if (this.config && this.config.river) {
      this.localConfig = JSON.parse(JSON.stringify(this.config.river))
    }
    if (!this.localConfig.selectedDevices || !this.localConfig.selectedDevices.length) {
      this.localConfig.selectedDevices = this.defaultDevices
    }
  },
  beforeDestroy () {
    clearInterval(this.refreshTimer)
  },
  components: {
    Multiselect,
    LineChart,
    DatePicker,
    JsonExcel,
    GoogleMap,
    WeatherWidget,
    ManualReadingModal
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
</style>
